/**
 * Every make Kabex imports, in display order.
 *
 * Order is the display order everywhere this list is read: the header's brand
 * menu, Shop by Make on the home page, and the first `FOOTER_BRAND_COUNT` of
 * them in the footer. It is also what `react-router.config.ts` prerenders a
 * page from, so a make added here is a new `/cars/<slug>` route.
 *
 * Names match the catalogue's `manufacturers.name`.
 *
 * ## Why the order is what it is
 *
 * It is a priority list, not an alphabet, because three of the four places
 * that read it show a prefix rather than the whole thing: the footer takes
 * the first eight, and Shop by Make shows the first 8/12/24 depending on the
 * breakpoint before the visitor asks for the rest. So the front of this list
 * is prime display space and the back is the long tail.
 *
 * - **1–19** are the makes that were here before the catalogue was widened
 *   (2026-10-21), in exactly the order Kabex settled on. They are the ones
 *   the brief names; nothing about this change was a reason to re-litigate
 *   them.
 * - **20–25** are the volume makes the widening added — the ones a Ugandan
 *   buyer asks for by name. They were already on the import-a-car page as
 *   dead chips (see `useSourceMarkets`) and are now pages.
 * - **26–37** are premium, luxury and American marques: real demand, but a
 *   thinner stream than the rows above them.
 * - **38–41** close with the machinery and the heavy units, which are not
 *   cars and should not be the first thing in a grid of cars. `Sinotruk` is
 *   the exception that stays up at 19, because it predates this list's
 *   growth and the header's "Trucks & Commercials" link names it.
 *
 * ## Adding the next one
 *
 * Two lines: the name here and its country in `brandOrigins.ts`, which is an
 * exhaustive record and will fail the build until you do. Artwork is
 * optional and always has been — `brandLogos` is partial, so a make with no
 * logo file is drawn as a typeset wordmark and ships the same day. Every one
 * of the forty-one below does have artwork as of 2026-10-24; that is a fact
 * about today, not a rule this list enforces.
 *
 * Deliberately free of imports: the build config reads this to prerender a
 * page per make, and the config loader cannot resolve the `@/` alias.
 */
export const featuredBrandNames = [
  // 1–19: the settled core.
  'Toyota',
  'Mercedes-Benz',
  'BMW',
  'Jeep',
  'Ford',
  'Audi',
  'Chevrolet',
  'Volvo',
  'Jaguar',
  'Mazda',
  'Lexus',
  'Subaru',
  'Isuzu',
  'Mitsubishi',
  'Nissan',
  'Peugeot',
  'BYD',
  'Jetour',
  'Sinotruk',
  // 20–25: volume makes.
  'Honda',
  'Suzuki',
  'Hyundai',
  'Kia',
  'Volkswagen',
  'Land Rover',
  // 26–37: premium, luxury and American.
  'Porsche',
  'Tesla',
  'Renault',
  'GMC',
  'Dodge',
  'Chrysler',
  'Cadillac',
  'Hummer',
  'Bentley',
  'Rolls-Royce',
  'Maybach',
  'Ferrari',
  // 38–41: exotics and machinery.
  'Lamborghini',
  'Maserati',
  'Caterpillar',
  'Massey Ferguson',
] as const;

/** A make's name, as every other file in this folder keys off it. */
export type BrandName = (typeof featuredBrandNames)[number];
